import React, { useState, useEffect } from 'react';

function extractYouTubeVideoId(urlOrId: string) {
  if (!urlOrId) return '';
  if (/^[\w-]{11}$/.test(urlOrId)) return urlOrId;

  // Remove unnecessary escape characters
  const regex = /(?:youtube.com\/(?:[^/\n\s]+\/\S+\/|(?:v|embed)\/|.*[?&]v=)|youtu.be\/)([\w-]{11})/;
  const match = urlOrId.match(regex);
  return match ? match[1] : '';
}

// Properly typed props
interface WebinarVideoModalProps {
  videoType: 'youtube' | 'mux';
  videoId: string;
  onClose: () => void;
}

const WebinarVideoModal: React.FC<WebinarVideoModalProps> = ({ videoType, videoId, onClose }) => {
  const cleanVideoId = videoType === 'youtube' ? extractYouTubeVideoId(videoId) : videoId;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      onClick={handleOverlayClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
      }}
    >
      <div
        style={{
          background: '#fff',
          padding: '1rem',
          borderRadius: '8px',
          maxWidth: '90%',
          minWidth: '50%',
          maxHeight: '90%',
          position: 'relative',
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            fontSize: '1.5rem',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
          }}
          aria-label='Close modal'
        >
          ×
        </button>

        {videoType === 'youtube' ? (
          <iframe
            width='100%'
            height='400'
            src={`https://www.youtube.com/embed/${cleanVideoId}`}
            title='YouTube video player'
            frameBorder='0'
            allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
            allowFullScreen
          />
        ) : (
          <iframe
            src={`https://player.mux.com/${videoId}`}
            width='100%'
            height='400'
            allow='autoplay; fullscreen'
            allowFullScreen
            title='Mux video player'
          />
        )}
      </div>
    </div>
  );
};

type ResponsiveSize = number | { desktop: number; tablet: number; mobile: number };

interface VideoPopupProps {
  thumbnail: string;
  videoId: string;
  videoType: 'youtube' | 'mux';
  thumbnailSize?: ResponsiveSize;
  roundedCorners?: boolean;
}

function useDeviceType() {
  const [device, setDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');

  useEffect(() => {
    function updateDevice() {
      if (window.innerWidth <= 768) setDevice('mobile');
      else if (window.innerWidth <= 1024) setDevice('tablet');
      else setDevice('desktop');
    }
    updateDevice();
    window.addEventListener('resize', updateDevice);
    return () => window.removeEventListener('resize', updateDevice);
  }, []);

  return device;
}

function getResponsiveValue(value: ResponsiveSize | undefined, device: 'desktop' | 'tablet' | 'mobile') {
  if (typeof value === 'number' || value === undefined) return value;
  return value[device];
}

const VideoPopup: React.FC<VideoPopupProps> = ({
  thumbnail,
  videoId,
  videoType,
  thumbnailSize = { desktop: 480, tablet: 360, mobile: 260 }, // ✅ larger default base
  roundedCorners = false,
}) => {
  const [showModal, setShowModal] = useState(false);
  const device = useDeviceType();
  const resolvedThumbnailSize = Number(getResponsiveValue(thumbnailSize, device)) || undefined;

  if (!thumbnail || !videoId || !videoType) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Video data missing.</div>;
  }

  return (
    <>
      <div
        style={{
          width: '100%',
          maxWidth: resolvedThumbnailSize || 480, // ✅ responsive max width
          aspectRatio: '16 / 9', // keeps nice proportions
          margin: '2rem auto',
          position: 'relative',
          cursor: 'pointer',
          borderRadius: roundedCorners ? 12 : 0,
          overflow: 'hidden',
          backgroundColor: 'transparent', // ✅ no black or gray boxes
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onClick={() => setShowModal(true)}
      >
        <img
          src={thumbnail}
          alt='Video thumbnail'
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain', // ✅ show full image, no cropping
            display: 'block',
            borderRadius: roundedCorners ? 12 : 0,
          }}
        />

        <svg
          width='64'
          height='64'
          viewBox='0 0 50 50'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 2,
            pointerEvents: 'none',
            filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.7))',
          }}
        >
          <path
            d='M10.3442 10.1865C10.5577 7.13019 14.0199 5.13123 16.9546 6.72461V6.72559L43.647 21.2422L43.6548 21.2471L43.6636 21.251C44.3437 21.6127 44.9123 22.1532 45.3091 22.8135C45.7058 23.4738 45.9155 24.2296 45.9155 25C45.9155 25.7704 45.7058 26.5262 45.3091 27.1865C44.9123 27.8468 44.3437 28.3873 43.6636 28.749L43.6548 28.7529L43.647 28.7578L16.9556 43.2725H16.9546C13.9262 44.921 10.3335 42.7383 10.3335 39.5166V10.4854L10.3442 10.1865Z'
            fill='#FF6319'
            stroke='white'
            strokeWidth='4'
          />
        </svg>
      </div>

      {showModal && <WebinarVideoModal videoType={videoType} videoId={videoId} onClose={() => setShowModal(false)} />}
    </>
  );
};

export default VideoPopup;
